import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { STATS } from "@/data/content";

function Counter({ to, start }) {
  const [val, setVal] = useState(start ? to : 0);
  const isYear = to >= 1000;

  useEffect(() => {
    if (!start) return;
    const controls = animate(0, to, {
      duration: 1.7,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
      onComplete: () => setVal(to),
    });
    return () => controls.stop();
  }, [start, to]);

  const rounded = Math.round(val);
  const text = isYear ? String(rounded) : String(rounded).padStart(2, "0");
  return <span>{text}</span>;
}

export default function Stats() {
  // One shared observer for the whole section — every counter starts together
  // when the strip enters the viewport. Fixes a flaky per-counter observer that
  // sometimes never fired for the BCA stat on mobile (leaving it stuck at "0").
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-15% 0px -15% 0px" });

  return (
    <section
      ref={sectionRef}
      data-testid="stats-strip"
      className="relative z-30 border-y border-white/10 bg-[#070707] py-16 md:py-24"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-y-12 px-6 md:grid-cols-4 md:gap-x-8 md:px-10">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`stat-${i}`}
            className="group relative border-l border-white/10 pl-6 md:border-l md:first:border-l"
          >
            <div className="font-display text-6xl md:text-7xl leading-none text-[#f2f0e6] transition-colors duration-300 group-hover:text-[#10b981]">
              <Counter to={s.value} start={inView} />
            </div>
            <div className="mt-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#f2f0e6]">
              {s.label}
            </div>
            <div className="mt-1 text-sm text-[#7c7a72]">{s.sub}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
